import { FirestoreDataConverter } from '@angular/fire/firestore';
import { InvoiceMode } from '../enums/invoice-mode.enum';
import { ObjectHelper } from "../helpers/object.helper";
import { FormControl } from "@angular/forms";
import { PaymentMode } from "../enums/payment-mode.enum";
import { CoverageStatusEnum } from '../enums/coverage-status.enum';
import { InvoicingAddress, InvoicingAddressForm } from "./invoicing-address.model";
import { DeliveryAddress } from "./delivery-address.model";
import { CustomerAccountType } from '../enums/customer-account-type.enum';

export type CustomerForm = {
  id: FormControl<string>;
  name: FormControl<string>;
  deliveryDelay: FormControl<number>;
  autoImportFiles: FormControl<boolean>;
  emailIsActive: FormControl<boolean>;
  callasWarning: FormControl<boolean>;
  notifyQuotations: FormControl<boolean>;
  invoiceEmailActive: FormControl<boolean>;
  invoiceMode: FormControl<InvoiceMode>;
  groupInvoices: FormControl<boolean>;
  paymentMode: FormControl<PaymentMode>;
  paymentFrequency: FormControl<number>;
  invoiceEmail: FormControl<string>;
  priceGrid: FormControl<number>;
  invoiceReferenceRequired: FormControl<boolean>;
  invoicingAddress: InvoicingAddressForm;
  organizationId: FormControl<string>;
  erpId: FormControl<string>;
  businessUnitId: FormControl<string>;
  deliveryAddresses: FormControl<DeliveryAddress[]>;
  vat: FormControl<number>;
  underTender: FormControl<boolean>;
  purchaseOrderEmails: FormControl<string[]>;
  accountType: FormControl<CustomerAccountType>;
}

/**
 * Model for customer
 */
export class Customer {
  /** Unique id of the customer (this id is the link with contact in Multipress) */
  id: string;
  /** The name of the customer */
  name: string;
  /** Estimated time for delivery */
  deliveryDelay: number;
  /** Files are automatically imported from Multipress when an order is created */
  autoImportFiles: boolean;
  /** Used to enable/disable pre-press emails notifications for a customer */
  emailIsActive: boolean;
  /** A human must verify all order processed by callas for the customer manually */
  callasWarning: boolean;
  /** If enabled, The user receives notifications about quotations */
  notifyQuotations: boolean;
  /** If enabled, The user receives notifications about invoices */
  invoiceEmailActive: boolean;
  /** Invoicing mode */
  invoiceMode: InvoiceMode;
  /** If enabled, the invoices are not grouped by reference or description */
  groupInvoices: boolean;
  /** Payment mode */
  paymentMode: PaymentMode;
  /** Delay before notification about payment */
  paymentFrequency: number;
  /** The email to send invoice */
  invoiceEmail: string;
  /** The price grid number (used for quotation) */
  priceGrid: number;
  /** An order reference is required to send the invoice */
  invoiceReferenceRequired: boolean;
  /** The invoicing address */
  invoicingAddress: InvoicingAddress;
  /** The organizationId */
  organizationId: string;
  /** The erp id */
  erpId: string;
  /** The business registration number (SIRET, 14 digits), sourced from Multipress */
  siret: string;
  /** The customer's invoiced-and-unpaid balance (encours factures), sourced from Multipress's credit_price */
  outstandingBalance?: number;
  /** The total amount of the customer's orders not yet invoiced (encours commandes) */
  uninvoicedOrdersAmount?: number;
  /** The identifier of the linked business unit */
  businessUnitId: string;
  /** Represents a collection of delivery addresses.*/
  deliveryAddresses: DeliveryAddress[];
  /** The VAT rate */
  vat: number;
  /** The customer is under tender (used for quotation) */
  underTender: boolean;
  /** Emails used to send purchase order */
  purchaseOrderEmails: string[];
  /** Account type used by production planning priority */
  accountType: CustomerAccountType;
  /** Coface coverage information */
  coverage: {
    easyNumber?: string;
    ceiling?: number;
    currency?: string;
    status: CoverageStatusEnum;
    pendingOrderId?: string;
    lastCheckedAt?: Date;
    lastDecisionDate?: Date;
    lastDeliveryId?: string;
  } | null;

  constructor(customer?: Partial<Customer>) {
    this.id = customer?.id ?? '';
    this.name = customer?.name ?? '';
    this.deliveryDelay = customer?.deliveryDelay ?? 5;
    this.autoImportFiles = customer?.autoImportFiles ?? false;
    this.emailIsActive = customer?.emailIsActive ?? false;
    this.callasWarning = customer?.callasWarning ?? false;
    this.notifyQuotations = customer?.notifyQuotations ?? false;
    this.paymentMode = customer?.paymentMode ?? PaymentMode.DIFFERED;
    this.paymentFrequency = customer?.paymentFrequency ?? 30;
    this.invoiceMode = customer?.invoiceMode ?? InvoiceMode.NONE;
    this.groupInvoices = customer?.groupInvoices ?? true;
    this.invoiceEmailActive = customer?.invoiceEmailActive ?? false;
    this.invoiceEmail = customer?.invoiceEmail ?? '';
    this.priceGrid = customer?.priceGrid ?? 99999;
    this.invoiceReferenceRequired = customer?.invoiceReferenceRequired ?? false;
    this.invoicingAddress = new InvoicingAddress(customer?.invoicingAddress);
    this.organizationId = customer?.organizationId ?? '';
    this.erpId = customer?.erpId ?? '';
    this.siret = customer?.siret ?? '';
    this.outstandingBalance = customer?.outstandingBalance ?? 0;
    this.uninvoicedOrdersAmount = customer?.uninvoicedOrdersAmount ?? 0;
    this.businessUnitId = customer?.businessUnitId ?? '';
    this.deliveryAddresses = customer?.deliveryAddresses ?? [];
    this.vat = customer?.vat ?? 20;
    this.underTender = customer?.underTender ?? false;
    this.purchaseOrderEmails = customer?.purchaseOrderEmails ?? [];
    this.accountType = customer?.accountType ?? CustomerAccountType.STANDARD;
    this.coverage = customer?.coverage ?? null;
  }

  /**
   * Converter used to convert a closing day from firebase
   */
  static converter: FirestoreDataConverter<Customer> = {
    toFirestore: (customer: Customer) => ({
      id: customer.id,
      name: customer.name,
      deliveryDelay: customer.deliveryDelay,
      autoImportFiles: customer.autoImportFiles,
      emailIsActive: customer.emailIsActive,
      callasWarning: customer.callasWarning,
      invoiceMode: customer.invoiceMode,
      groupInvoices: customer.groupInvoices,
      paymentMode: customer.paymentMode,
      paymentFrequency: customer.paymentFrequency,
      invoiceEmailActive: customer.invoiceEmailActive,
      invoiceEmail: customer.invoiceEmail,
      priceGrid: customer.priceGrid,
      notifyQuotations: customer.notifyQuotations,
      invoiceReferenceRequired: customer.invoiceReferenceRequired,
      invoicingAddress: ObjectHelper.anonymize(customer.invoicingAddress),
      organizationId: customer.organizationId,
      erpId: customer.erpId,
      siret: customer.siret,
      outstandingBalance: customer.outstandingBalance ?? 0,
      uninvoicedOrdersAmount: customer.uninvoicedOrdersAmount ?? 0,
      businessUnitId: customer.businessUnitId,
      deliveryAddresses: customer.deliveryAddresses,
      vat: customer.vat,
      underTender: customer.underTender,
      purchaseOrderEmails: customer.purchaseOrderEmails,
      accountType: customer.accountType,
      coverage: customer.coverage
    }),
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      const coverage = data['coverage'];
      return new Customer({
        ...data,
        id: snapshot.id,
        coverage: coverage ? {
          ...coverage,
          lastCheckedAt: coverage.lastCheckedAt ? new Date(coverage.lastCheckedAt.seconds * 1000) : null,
          lastDecisionDate: coverage.lastDecisionDate ? new Date(coverage.lastDecisionDate.seconds * 1000) : null
        } : coverage
      });
    }
  };
}
